import { Trans, msg } from '@lingui/macro';
import { Redirect, Route, Switch, useParams } from 'react-router-dom';
import { Box, Flex, Text } from '~/design-system';
import {
  ProgressIndicator as BaseProgressIndicator,
  ProgressStep,
} from '../../../../components/Layout/ProgressIndicator';
import { getStepLinks } from '../../../_config';
import NotFound from '../../404';
import { useCommonLinks } from '../../_config';
import Complete from './complete';
import IdentifierValidation from './identifierValidation';
import IdentifyFields from './identifyFields';
import MapFields from './mapFields';
import MapHeader from './mapHeader';
import MapValues from './mapValues';
import Name from './name';
import Ready from './ready';
import Validation from './validation';
export { default as bottlebooksFieldsSource } from './fields/bottlebooksFieldsSource';

export function $ImportIdRoutes({ match }) {
  return (
    <Switch>
      <Route path={`${match.path}/name`} component={Name} />
      <Route path={`${match.path}/mapHeader`} component={MapHeader} />
      <Route path={`${match.path}/identifyFields`} component={IdentifyFields} />
      <Route
        path={`${match.path}/identifierValidation`}
        component={IdentifierValidation}
      />
      <Route path={`${match.path}/mapFields`} component={MapFields} />
      <Route path={`${match.path}/validation`} component={Validation} />
      <Route path={`${match.path}/mapValues`} component={MapValues} />
      <Route path={`${match.path}/ready`} component={Ready} />
      <Route path={`${match.path}/complete`} component={Complete} />
      {/* The starting route: */}
      <Redirect exact from={`${match.path}`} to={`${match.url}/mapHeader`} />
      <Route component={NotFound} />
    </Switch>
  );
}

export const importType = 'profileDataImport';

/** @typedef {keyof ReturnType<getProductDataImportLinks>} Step The possible steps for profileDataImport. */

/**
 * @param {object} params
 * @param {string} params.workspaceId
 * @param {string} params.importId
 * @returns
 */
export function useGetProductDataImportLinks({ workspaceId, importId }) {
  const common = useCommonLinks();
  const baseLink = `/${workspaceId}`;
  const baseImportId = importId;
  return {
    root: common.root,
    uploadFile: () => `${baseLink}/${importType}/uploadFile`,
    /**
     * We might not have set the importId yet if we've just uploaded a new file. We may pass an importId in that case.
     * @param {{ importId?: string}} [options]
     */
    name: (options) =>
      `${baseLink}/${importType}/${options?.importId || baseImportId}/name`,
    mapHeader: () => `${baseLink}/${importType}/${importId}/mapHeader`,
    identifyFields: () =>
      `${baseLink}/${importType}/${importId}/identifyFields`,
    identifierValidation: () =>
      `${baseLink}/${importType}/${importId}/identifierValidation`,
    mapFields: () => `${baseLink}/${importType}/${importId}/mapFields`,
    mapValues: () => `${baseLink}/${importType}/${importId}/mapValues`,
    validation: () => `${baseLink}/${importType}/${importId}/validation`,
    ready: () => `${baseLink}/${importType}/${importId}/ready`,
    complete: () => `${baseLink}/${importType}/${importId}/complete`,
  };
}

export function ProgressIndicator({ step }) {
  const steps = ['uploadFile', 'identifyFields', 'mapData', 'ready'];
  const currentIndex = steps.indexOf(step);
  const currentStep = currentIndex + 1;

  return (
    <Flex sx={{ justifyContent: 'space-between', paddingX: 40 }}>
      <Box sx={{ width: 150, flexShrink: 0, textAlign: 'left' }}>
        <Trans>Product Updates</Trans>
      </Box>
      <BaseProgressIndicator currentIndex={currentIndex}>
        <ProgressStep>
          <Trans>Upload file</Trans>
        </ProgressStep>
        <ProgressStep>
          <Trans>Identify data</Trans>
        </ProgressStep>
        <ProgressStep>
          <Trans>Map data</Trans>
        </ProgressStep>
        <ProgressStep>
          <Trans>Confirm</Trans>
        </ProgressStep>
      </BaseProgressIndicator>
      <Box sx={{ width: 150, flexShrink: 0, textAlign: 'right' }}>
        <Text>
          Step {currentStep} of {steps.length}
        </Text>
      </Box>
    </Flex>
  );
}

/**
 * @param {Step} currentStep
 * @returns
 */
export function useLink(currentStep) {
  /** @type {{importId: string}} */
  const { workspaceId, importId } = useParams();
  const urls = useGetProductDataImportLinks({ workspaceId, importId });
  return getStepLinks(urls, Object.keys(urls), currentStep);
}

export const fieldsToImport = [
  {
    key: 'profile.brandId',
    label: msg`Bottlebooks Brand ID`,
    required: true,
  },
  // { key: 'product.$uniqueId', label: 'Product Unique ID', required: true },
  // { key: 'product.instanceId', label: 'Product ID', required: false },
];
